import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import UserCreditSettingsDialog from './UserCreditSettingsDialog';
import { formatDistanceToNow } from 'date-fns';
import { usersApi } from '../services/api';
import { User, UserStats } from '../types';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [settingsUser, setSettingsUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getUsers();
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
      console.error('Users load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (user: User) => {
    try {
      const statsResponse = await usersApi.getUserStats(user.id);
      setUserStats(statsResponse.data);
      setSelectedUser(user);
    } catch (err) {
      console.error('Failed to load user stats:', err);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'user':
        return 'primary';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Users
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* User Statistics Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Users
            </Typography>
            <Typography variant="h4">
              {users.length}
            </Typography>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Active Users
            </Typography>
            <Typography variant="h4" color="primary.main">
              {users.filter(u => u.lastActive && new Date(u.lastActive) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
            </Typography>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Recordings
            </Typography>
            <Typography variant="h4" color="success.main">
              {users.reduce((sum, user) => sum + user.totalRecordings, 0)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Recordings</TableCell>
                <TableCell>Total Duration</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          getInitials(user.name)
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={getRoleColor(user.role) as any}
                      size="small"
                      icon={user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.totalRecordings}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDuration(user.totalDuration)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.lastActive 
                        ? formatDistanceToNow(new Date(user.lastActive), { addSuffix: true })
                        : 'Never'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Credit & transcription limits">
                      <IconButton
                        size="small"
                        onClick={() => setSettingsUser(user)}
                        aria-label="Edit credit and transcription limits"
                      >
                        <TuneIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View user details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewUser(user)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <UserCreditSettingsDialog
        open={settingsUser != null}
        user={settingsUser}
        onClose={() => setSettingsUser(null)}
      />

      {/* User Details Dialog */}
      {selectedUser && userStats && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            User Details: {selectedUser.name}
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                User Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2">{selectedUser.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">Role: {selectedUser.role}</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Member since {formatDistanceToNow(new Date(selectedUser.createdAt), { addSuffix: true })}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Recording Statistics
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Recordings
                  </Typography>
                  <Typography variant="h6">
                    {userStats.totalJobs}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Completed
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {userStats.completedJobs}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Failed
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {userStats.failedJobs}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Duration
                  </Typography>
                  <Typography variant="h6">
                    {formatDuration(userStats.totalDuration)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Users; 